import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CountUp } from '@/components/generals/count-up'
import { CtaBand } from '@/components/generals/cta-band'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Landscape } from '@/components/generals/landscape'
import { PageHero } from '@/components/generals/page-hero'
import { Reveal } from '@/components/generals/reveal'
import { ScrollText } from '@/components/generals/scroll-text'
import { SectionHeading } from '@/components/generals/section-heading'
import { WashedPortrait } from '@/components/generals/washed-portrait'
import { STATS, TEAM, VALUES } from '@/constants/team'
import { LANDSCAPES } from '@/lib/visual'

const MISSION = 'Most money advice is either too loud or too expensive. We think everyone should have the unhurried, *personal guidance* that once belonged only to the wealthy. So we built an AI that asks before it answers, speaks in plain words and knows the moment to bring in a person. *Clarity is what we make.* The rest follows from it.'

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About us'
                description='Why Verdane exists, the people building it and the promises we hold to as an AI-powered financial advisor with real humans behind it.'
            />

            <PageHero
                eyebrow='About'
                title='Here to make money *feel lighter*'
                subtitle='A banker and an investor started Verdane after years of seeing bright, capable people lose sleep over their finances.'
                image='/images/uploads/replace-c.png'
                position='center 40%'
            />

            <section aria-label='Our mission' className='relative py-24 sm:py-36'>
                <div className='shell'>
                    <Eyebrow align='left'>
                        Why we exist
                    </Eyebrow>

                    <ScrollText
                        text={MISSION}
                        className='mt-10 max-w-5xl text-[2rem] font-normal leading-[1.15] tracking-[-0.035em] text-cream sm:text-5xl md:text-[3.6rem] md:leading-[1.08]'
                    />
                </div>
            </section>

            <section aria-label='Verdane in numbers' className='relative isolate overflow-hidden py-20'>
                <div aria-hidden='true' className='absolute inset-0 -z-10 opacity-60'>
                    <Landscape src={LANDSCAPES.wheatHaze} blur='lg' fade='top-bottom' />
                </div>

                <dl className='shell grid grid-cols-2 gap-y-12 lg:grid-cols-4'>
                    {STATS.map((stat, index) => (
                        <Reveal key={stat.label} delay={index * 120} className='flex flex-col items-center text-center lg:border-l lg:border-white/10 lg:first:border-l-0'>
                            <dt className='order-2 mt-3 text-[11px] font-medium uppercase tracking-[0.3em] text-cream/55'>
                                {stat.label}
                            </dt>

                            <dd className='order-1 text-5xl font-normal tracking-[-0.05em] text-cream sm:text-6xl'>
                                <CountUp value={stat.value} decimals={stat.decimals} prefix={stat.prefix} suffix={stat.suffix} duration={2000} />
                            </dd>
                        </Reveal>
                    ))}
                </dl>
            </section>

            <section aria-label='The team' className='relative py-24 sm:py-32'>
                <div className='shell'>
                    <SectionHeading
                        eyebrow='Who we are'
                        title='The people who *keep it steady*'
                        subtitle='Former bankers, certified planners, researchers and designers who think good advice should sound like a conversation, never a pitch.'
                    />

                    <ul className='mt-16 grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4'>
                        {TEAM.map((person, index) => (
                            <Reveal as='li' key={person.name} delay={(index % 4) * 110} y={36}>
                                <figure className='grain group relative isolate aspect-[3/4] overflow-hidden rounded-[26px] bg-ink'>
                                    <WashedPortrait
                                        src={person.image}
                                        alt={`Portrait of ${person.name}, ${person.role}`}
                                        wash={person.wash}
                                        intensity='soft'
                                        position={person.position}
                                        sizes='(min-width: 1024px) 25vw, 50vw'
                                        imageClassName='group-hover:scale-[1.05]'
                                    />
                                    <div aria-hidden='true' className='absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_45%,rgba(0,0,0,0.88)_100%)]' />
                                    <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                                    <figcaption className='absolute inset-x-4 bottom-4 sm:inset-x-5 sm:bottom-5'>
                                        <span className='block font-serif text-xl italic leading-tight text-cream sm:text-2xl'>
                                            {person.name}
                                        </span>

                                        <span className='mt-1 block text-[11px] leading-snug text-cream/60 sm:text-xs'>
                                            {person.role}
                                        </span>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section aria-label='Our values' className='relative py-24 sm:py-32'>
                <div className='shell'>
                    <SectionHeading
                        eyebrow='What we stand for'
                        title='Four things *we hold to*'
                    />

                    <ul className='mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {VALUES.map((value, index) => (
                            <Reveal as='li' key={value.title} delay={index * 120} className='glass grain relative flex min-h-[260px] flex-col justify-between overflow-hidden rounded-[28px] p-7'>
                                <span className='font-serif text-4xl italic leading-none text-citron'>
                                    0{index + 1}
                                </span>

                                <div className='mt-10'>
                                    <h3 className='text-xl tracking-[-0.02em] text-cream'>
                                        {value.title}
                                    </h3>

                                    <p className='mt-2 text-sm leading-relaxed text-cream/60'>
                                        {value.body}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <CtaBand
                eyebrow='Begin today'
                title={'Let your money\n*finally settle*'}
                image={LANDSCAPES.heroHills}
                position='center 62%'
            />
        </Layout>
    )
}

export default About
